import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { clientsApi, ClientInput } from '../services/api';
import {
  Client,
  ClientRemoteTiers,
  ClientRemoteTiersEtat,
  ClientRemoteTiersResultat,
  ClientRemoteTiersResultatCode,
  WILAYAS,
} from '../types';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import AuditHistoryModal from '../components/AuditHistoryModal';
import './Pages.css';

const ITEMS_PER_PAGE = 10;
const DEFAULT_BULK_EMAIL_SUBJECT = 'Information contrats actifs Winalco';
const DEFAULT_BULK_EMAIL_BODY =
  'Bonjour,\n\nVeuillez trouver ce message concernant votre contrat actif Winalco.\n\nCordialement,\nEquipe Winalco';
const DEFAULT_DIRECT_EMAIL_SUBJECT = 'Message Winalco';
const DEFAULT_BULK_SMS_BODY =
  'Bonjour, veuillez trouver ce message concernant votre contrat actif Winalco. Equipe Winalco.';
const buildDirectEmailBody = (clientName: string) =>
  `Bonjour ${clientName || 'Client'},\n\nVeuillez trouver votre message Winalco.\n\nCordialement,\nEquipe Winalco`;
const buildDirectSmsBody = (clientName: string) =>
  `Bonjour ${clientName || 'Client'}, veuillez trouver votre message Winalco. Equipe Winalco.`;

type ClientContractFilter = 'all' | 'with_contrats' | 'without_contrats';

// Filtre sur l'etat dans Remote : "non envoye" = aucun tiers la-bas (jamais
// envoye, ou refuse des le premier envoi) ; un refus plus tard garde le tiers.
type ClientRemoteFilter = 'all' | 'non_envoyes' | 'refuses' | 'envoyes';

// Envoi vers les tiers du remote-server (Remote Desktop) : Gestion Licences est
// maitre, a sens unique, envoi au bouton. Le serveur accepte 200 clients par
// appel ; une selection plus grande part en plusieurs appels.
const REMOTE_LOT_MAX = 200;

const REMOTE_RESULTAT_LABELS: Record<ClientRemoteTiersResultatCode, string> = {
  cree: 'Tiers cree',
  adopte: 'Rattache au tiers existant du meme nom',
  mis_a_jour: 'Tiers mis a jour',
  inchange: 'Deja a jour',
  refuse: 'Refuse',
  introuvable: 'Client introuvable (supprime entre-temps)',
};

// Codes de refus du remote-server (memes regles que son portail)
const REMOTE_ERREUR_LABELS: Record<string, string> = {
  denomination_required: 'nom vide',
  bad_denomination: 'nom refuse (2 a 100 caracteres)',
  bad_phone: 'telephone refuse (6 a 30 caracteres, au moins 6 chiffres, un seul numero)',
  bad_email: 'email invalide',
  bad_ref: 'reference refusee',
  tiers_denomination_prise: 'un autre tiers porte deja ce nom dans Remote',
  tiers_limit: 'nombre maximum de tiers atteint dans Remote',
  reponse_invalide: 'reponse inattendue du serveur Remote',
  // Contrat du tiers (dates du contrat du client)
  bad_contract: 'contrat refuse par Remote',
  bad_contract_date: 'date de contrat invalide',
  contract_dates_reversed: 'contrat qui finit avant de commencer',
};

// Version courte, pour la colonne Remote (le detail reste en infobulle)
const REMOTE_ERREUR_COURTS: Record<string, string> = {
  denomination_required: 'nom vide',
  bad_denomination: 'nom refuse',
  bad_phone: 'telephone refuse',
  bad_email: 'email invalide',
  bad_ref: 'reference refusee',
  tiers_denomination_prise: 'nom deja pris',
  tiers_limit: 'limite atteinte',
  reponse_invalide: 'reponse invalide',
  bad_contract: 'contrat refuse',
  bad_contract_date: 'date de contrat',
  contract_dates_reversed: 'dates inversees',
};

const remoteErreurLabel = (code?: string | null) =>
  (code && REMOTE_ERREUR_LABELS[code]) || code || 'refus sans motif';

const remoteErreurCourt = (code?: string | null) =>
  (code && REMOTE_ERREUR_COURTS[code]) || remoteErreurLabel(code);

// "AAAA-MM-JJTHH:MM:SS" (heure d'Alger) -> "JJ/MM/AAAA HH:MM"
const formatRemoteDate = (value?: string | null) =>
  value && value.length >= 16
    ? `${value.slice(8, 10)}/${value.slice(5, 7)}/${value.slice(0, 4)} ${value.slice(11, 16)}`
    : '';

interface EmailComposerState {
  open: boolean;
  subject: string;
  message: string;
  targetClient: Client | null;
}

interface SmsComposerState {
  open: boolean;
  message: string;
  targetClient: Client | null;
}

const normalizeClients = (data: Client[]): Client[] =>
  data.map((client) => ({
    ...client,
    has_contrats: Boolean(client.has_contrats),
    has_active_non_expired_contrat: Boolean(client.has_active_non_expired_contrat),
  }));

const HistoryIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={{ width: 16, height: 16 }}>
    <path d="M9.3 3.75h5.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    <path d="M12 4.2v1.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    <circle cx="12" cy="12.8" r="6.9" fill="#ffffff" stroke="currentColor" strokeWidth="1.7" />
    <circle cx="12" cy="12.8" r="1.1" fill="#d84b57" />
    <path d="M12 12.8V9.9" stroke="#d84b57" strokeWidth="1.7" strokeLinecap="round" />
    <path d="M12 12.8l2.15 1.55" stroke="#d84b57" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const EditIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={{ width: 16, height: 16 }}>
    <path
      d="M4 17.25V20h2.75L17.8 8.94l-2.75-2.75L4 17.25Zm14.71-9.04a1 1 0 0 0 0-1.42l-1.5-1.5a1 1 0 0 0-1.42 0l-1.17 1.17 2.75 2.75 1.34-1.34Z"
      fill="currentColor"
    />
  </svg>
);

const DeleteIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={{ width: 16, height: 16 }}>
    <path
      d="M9 3.75h6l.6 1.5H19.5v1.5h-15v-1.5h3.9L9 3.75Zm-1.5 6h1.5v7.5H7.5v-7.5Zm4.5 0h1.5v7.5H12v-7.5Zm4.5 0H18v7.5h-1.5v-7.5Z"
      fill="currentColor"
    />
    <path d="M6 6.75h12v12a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 6 18.75v-12Z" fill="none" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const MailIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={{ width: 16, height: 16 }}>
    <path
      d="M4.5 6.75h15a1.5 1.5 0 0 1 1.5 1.5v7.5a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 15.75v-7.5a1.5 1.5 0 0 1 1.5-1.5Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path d="m4.5 8.25 7.5 5.25 7.5-5.25" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SmsIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={{ width: 16, height: 16 }}>
    <path
      d="M6 5.25h12a2.25 2.25 0 0 1 2.25 2.25v6A2.25 2.25 0 0 1 18 15.75H10.5l-4.5 3v-3H6A2.25 2.25 0 0 1 3.75 13.5v-6A2.25 2.25 0 0 1 6 5.25Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path d="M8.25 9h7.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M8.25 12h4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const KeyIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={{ width: 16, height: 16 }}>
    <circle cx="8.25" cy="12" r="3.75" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <path d="M12 12h8.25" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M17.25 12v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M20.25 12v2.25" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const Clients: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [communicationClients, setCommunicationClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [contractFilter, setContractFilter] = useState<ClientContractFilter>('all');
  const [remoteFilter, setRemoteFilter] = useState<ClientRemoteFilter>('all');
  const [sendingBulkEmail, setSendingBulkEmail] = useState(false);
  const [sendingBulkSms, setSendingBulkSms] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { showToast, showConfirm } = useToast();
  const { hasPermission } = useAuth();
  const canBulkCommunicate = hasPermission('clients', 'edit');
  const canResetPortalAccess = hasPermission('clients', 'reset_portal');
  const hasRemoteTiersPermission = hasPermission('clients', 'remote_tiers');

  const [remoteEtat, setRemoteEtat] = useState<ClientRemoteTiersEtat | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [sendingRemote, setSendingRemote] = useState(false);
  const [remoteResults, setRemoteResults] = useState<ClientRemoteTiersResultat[] | null>(null);
  // Bouton, cases et colonne Remote : droit, cle posee et migration appliquee
  const canSendRemote = hasRemoteTiersPermission && Boolean(remoteEtat?.configure && remoteEtat?.disponible);

  const [auditClient, setAuditClient] = useState<Client | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [formData, setFormData] = useState<ClientInput>({
    client_name: '',
    telephone: '',
    email: '',
    wilaya: '',
    ville: '',
  });

  const [emailComposer, setEmailComposer] = useState<EmailComposerState>({
    open: false,
    subject: DEFAULT_BULK_EMAIL_SUBJECT,
    message: DEFAULT_BULK_EMAIL_BODY,
    targetClient: null,
  });
  const [smsComposer, setSmsComposer] = useState<SmsComposerState>({
    open: false,
    message: DEFAULT_BULK_SMS_BODY,
    targetClient: null,
  });

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await clientsApi.getAll(search || undefined);
      const data = Array.isArray(response.data) ? response.data : response.data?.data || response.data?.clients || [];
      setClients(normalizeClients(data));
    } catch (err: any) {
      console.error('Erreur chargement clients:', err?.response?.status, err?.response?.data, err?.message);
      const msg = err?.response?.status
        ? `Erreur ${err.response.status}: ${err.response.data?.message || err.message}`
        : `Erreur reseau: ${err?.message || 'Serveur inaccessible'}`;
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [search, showToast]);

  const loadCommunicationClients = useCallback(async () => {
    if (!canBulkCommunicate) {
      setCommunicationClients([]);
      return;
    }

    try {
      const response = await clientsApi.getAll();
      const data = Array.isArray(response.data) ? response.data : response.data?.data || response.data?.clients || [];
      setCommunicationClients(normalizeClients(data));
    } catch (err) {
      console.error('Erreur chargement clients pour communication:', err);
    }
  }, [canBulkCommunicate]);

  // Etat des envois vers Remote : sans lui (droit absent, erreur), la page
  // reste celle d'avant, sans bouton ni colonne
  const loadRemoteTiers = useCallback(async () => {
    if (!hasRemoteTiersPermission) {
      setRemoteEtat(null);
      return;
    }

    try {
      const response = await clientsApi.getRemoteTiers();
      setRemoteEtat(response.data);
    } catch (err) {
      console.error('Erreur chargement envois Remote:', err);
      setRemoteEtat(null);
    }
  }, [hasRemoteTiersPermission]);

  const refreshClientData = useCallback(async () => {
    await Promise.all([loadClients(), loadCommunicationClients(), loadRemoteTiers()]);
  }, [loadClients, loadCommunicationClients, loadRemoteTiers]);

  useEffect(() => {
    void loadClients();
  }, [loadClients]);

  useEffect(() => {
    void loadCommunicationClients();
  }, [loadCommunicationClients]);

  useEffect(() => {
    void loadRemoteTiers();
  }, [loadRemoteTiers]);

  const remoteParClient = useMemo(() => {
    const map = new Map<number, ClientRemoteTiers>();
    (remoteEtat?.envois || []).forEach((envoi) => map.set(envoi.client_id, envoi));
    return map;
  }, [remoteEtat]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, contractFilter, remoteFilter]);

  // Sans colonne Remote (droit, cle ou migration absents), son filtre ne joue pas
  const activeRemoteFilter: ClientRemoteFilter = canSendRemote ? remoteFilter : 'all';

  const filteredClients = clients.filter((client) => {
    if (contractFilter === 'with_contrats' && !client.has_contrats) {
      return false;
    }

    if (contractFilter === 'without_contrats' && client.has_contrats) {
      return false;
    }

    const envoi = remoteParClient.get(client.id);
    if (activeRemoteFilter === 'non_envoyes') {
      return !envoi?.tiers_id;
    }

    if (activeRemoteFilter === 'refuses') {
      return envoi?.dernier_resultat === 'refuse';
    }

    if (activeRemoteFilter === 'envoyes') {
      return Boolean(envoi?.tiers_id);
    }

    return true;
  });

  const activeContractClients = communicationClients.filter((client) => client.has_active_non_expired_contrat);
  const activeContractEmailClients = activeContractClients.filter((client) => client.email?.trim());

  const totalPages = Math.ceil(filteredClients.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedClients = filteredClients.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Selection pour Remote : elle survit aux pages, a la recherche et au filtre ;
  // la case d'en-tete coche ou decoche tous les clients filtres.
  const selectedFilteredCount = filteredClients.filter((client) => selectedIds.has(client.id)).length;
  const allFilteredSelected = filteredClients.length > 0 && selectedFilteredCount === filteredClients.length;

  const toggleClientSelection = (clientId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) {
        next.delete(clientId);
      } else {
        next.add(clientId);
      }
      return next;
    });
  };

  const toggleAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredClients.forEach((client) => {
        if (allFilteredSelected) {
          next.delete(client.id);
        } else {
          next.add(client.id);
        }
      });
      return next;
    });
  };

  const handleAdd = () => {
    setEditingClient(null);
    setFormData({
      client_name: '',
      telephone: '',
      email: '',
      wilaya: '',
      ville: '',
    });
    setShowModal(true);
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setFormData({
      client_name: client.client_name,
      telephone: client.telephone,
      email: client.email,
      wilaya: client.wilaya,
      ville: client.ville,
    });
    setShowModal(true);
  };

  const handleDelete = (id: number) => {
    showConfirm('Etes-vous sur de vouloir supprimer ce client ?', async () => {
      try {
        const response = await clientsApi.delete(id);
        // Client envoye vers Remote dont le tiers n'a pas pu etre archive
        const remoteWarning = response.data?.remote_warning;
        showToast(remoteWarning || 'Client supprime avec succes', remoteWarning ? 'warning' : 'success');
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        await refreshClientData();
      } catch (err: any) {
        // 409 : le serveur dit ce qui retient le client (contrats, licences, acces distant)
        showToast(err.response?.data?.error || 'Erreur lors de la suppression', 'error');
        if (err.response?.status === 404) {
          await refreshClientData();
        }
      }
    }, 'Supprimer le client');
  };

  const handleResetPortalAccess = (client: Client) => {
    showConfirm(
      `Reinitialiser l'acces au portail de "${client.client_name}" ?\n\n` +
        'Son mot de passe redevient "1" et ses sessions portail sont fermees. ' +
        'Il devra choisir un nouveau mot de passe a sa prochaine connexion.',
      async () => {
        try {
          const response = await clientsApi.resetPortalAccess(client.id);
          const revokedCount = Number(response.data?.revoked_session_count || 0);
          showToast(
            revokedCount > 0
              ? `Acces portail reinitialise (mot de passe "1"), ${revokedCount} session(s) fermee(s)`
              : 'Acces portail reinitialise (mot de passe "1")',
            'success'
          );
        } catch (err: any) {
          showToast(
            err.response?.data?.error || "Erreur lors de la reinitialisation de l'acces portail",
            'error'
          );
        }
      },
      "Reinitialiser l'acces portail"
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await clientsApi.update(editingClient.id, formData);
        showToast('Client mis a jour avec succes', 'success');
        setShowModal(false);
        await refreshClientData();
      } else {
        const checkResponse = await clientsApi.checkExists(formData.client_name);
        if (checkResponse.data.exists) {
          showConfirm('Un client avec ce nom existe deja. Voulez-vous continuer ?', async () => {
            try {
              const response = await clientsApi.create(formData);
              // Client cree, parti de lui-meme vers Remote : il n'y est peut-etre pas arrive
              const remoteWarning = response.data?.remote_warning;
              showToast(remoteWarning || 'Client cree avec succes', remoteWarning ? 'warning' : 'success');
              setShowModal(false);
              await refreshClientData();
            } catch (err) {
              showToast('Erreur lors de la creation', 'error');
            }
          }, 'Client existant');
        } else {
          const response = await clientsApi.create(formData);
          const remoteWarning = response.data?.remote_warning;
          showToast(remoteWarning || 'Client cree avec succes', remoteWarning ? 'warning' : 'success');
          setShowModal(false);
          await refreshClientData();
        }
      }
    } catch (err) {
      showToast('Erreur lors de l enregistrement', 'error');
    }
  };

  const handleSendToRemote = () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      showToast('Aucun client selectionne', 'warning');
      return;
    }

    showConfirm(
      `Envoyer ${ids.length} client${ids.length > 1 ? 's' : ''} vers Remote ?\n\n` +
        'Chaque client devient un tiers dans Remote, ou met a jour le sien (nom, telephone, email). ' +
        'Un tiers saisi a la main dans Remote sous le meme nom est rattache au client.',
      async () => {
        const results: ClientRemoteTiersResultat[] = [];
        try {
          setSendingRemote(true);
          for (let start = 0; start < ids.length; start += REMOTE_LOT_MAX) {
            const response = await clientsApi.sendToRemote(ids.slice(start, start + REMOTE_LOT_MAX));
            results.push(...(response.data?.resultats || []));
          }
        } catch (err: any) {
          showToast(
            err?.response?.data?.error || err?.message || 'Erreur lors de l envoi vers Remote',
            'error'
          );
        } finally {
          setSendingRemote(false);
        }

        if (results.length > 0) {
          // Les clients acceptes sortent de la selection ; les refuses y restent
          // pour etre corriges puis renvoyes.
          const acceptes = new Set(
            results.filter((r) => r.resultat !== 'refuse' && r.resultat !== 'introuvable').map((r) => r.client_id)
          );
          setSelectedIds((prev) => new Set(Array.from(prev).filter((id) => !acceptes.has(id))));
          setRemoteResults(results);
        }
        await loadRemoteTiers();
      },
      'Envoyer vers Remote'
    );
  };

  const renderRemoteCell = (client: Client) => {
    const envoi = remoteParClient.get(client.id);
    if (!envoi) {
      return <span className="remote-tiers-none">-</span>;
    }

    if (envoi.dernier_resultat === 'refuse') {
      const precedent = envoi.envoye_le ? ` Dernier envoi accepte le ${formatRemoteDate(envoi.envoye_le)}.` : '';
      return (
        <div
          className="remote-tiers-cell"
          title={`Refuse : ${remoteErreurLabel(envoi.derniere_erreur)}. Essai du ${formatRemoteDate(envoi.tente_le)}.${precedent}`}
        >
          <span className="status-badge inactive">Refuse</span>
          <small>{remoteErreurCourt(envoi.derniere_erreur)}</small>
        </div>
      );
    }

    return (
      <div className="remote-tiers-cell">
        {envoi.archive ? (
          <span className="status-badge archived" title="Tiers archive dans le portail Remote">
            Archive
          </span>
        ) : (
          <span className="status-badge active">Envoye</span>
        )}
        <small>{formatRemoteDate(envoi.envoye_le)}</small>
      </div>
    );
  };

  const handleBulkEmail = () => {
    if (activeContractEmailClients.length === 0) {
      showToast('Aucun client avec contrat actif non expire et email disponible', 'warning');
      return;
    }

    setEmailComposer({
      open: true,
      subject: DEFAULT_BULK_EMAIL_SUBJECT,
      message: DEFAULT_BULK_EMAIL_BODY,
      targetClient: null,
    });
  };

  const handleSingleEmail = (client: Client) => {
    if (!client.email?.trim()) {
      showToast('Aucune adresse email disponible pour ce client', 'warning');
      return;
    }

    setEmailComposer({
      open: true,
      subject: DEFAULT_DIRECT_EMAIL_SUBJECT,
      message: buildDirectEmailBody(client.client_name),
      targetClient: client,
    });
  };

  const closeEmailComposer = () => {
    if (sendingBulkEmail) {
      return;
    }

    setEmailComposer({
      open: false,
      subject: DEFAULT_BULK_EMAIL_SUBJECT,
      message: DEFAULT_BULK_EMAIL_BODY,
      targetClient: null,
    });
  };

  const submitBulkEmail = async (e: React.FormEvent) => {
    e.preventDefault();

    const subject = emailComposer.subject.trim();
    const message = emailComposer.message.trim();
    const targetClient = emailComposer.targetClient;
    if (!subject || !message) {
      showToast('Veuillez saisir le sujet et le message de l email', 'warning');
      return;
    }

    try {
      setSendingBulkEmail(true);
      const response = targetClient
        ? await clientsApi.sendEmail(targetClient.id, subject, message)
        : await clientsApi.sendBulkEmail(
          activeContractEmailClients.map((client) => client.id),
          subject,
          message
        );
      const firstFailureMessage = response.data?.first_failure_message?.trim();
      const resultMessage = firstFailureMessage
        ? `${response.data?.message || 'Email en bloc traite avec succes'} Detail SMTP: ${firstFailureMessage}`
        : response.data?.message || (targetClient ? 'Email envoye avec succes' : 'Email en bloc traite avec succes');
      const hasPartialIssue = targetClient
        ? false
        : (response.data?.failed_count || 0) > 0 ||
          (response.data?.skipped_count || 0) > 0 ||
          (response.data?.invalid_email_count || 0) > 0;
      setEmailComposer({
        open: false,
        subject: DEFAULT_BULK_EMAIL_SUBJECT,
        message: DEFAULT_BULK_EMAIL_BODY,
        targetClient: null,
      });
      showToast(resultMessage, hasPartialIssue ? 'warning' : 'success');
      await loadCommunicationClients();
    } catch (err: any) {
      showToast(
        err?.response?.data?.error || err?.response?.data?.message || 'Erreur lors de l envoi des emails en bloc',
        'error'
      );
    } finally {
      setSendingBulkEmail(false);
    }
  };

  const handleBulkSms = () => {
    if (activeContractClients.length === 0) {
      showToast('Aucun client avec contrat actif non expire pour le SMS', 'warning');
      return;
    }

    setSmsComposer({
      open: true,
      message: DEFAULT_BULK_SMS_BODY,
      targetClient: null,
    });
  };

  const handleSingleSms = (client: Client) => {
    if (!client.telephone?.trim()) {
      showToast('Aucun numero de telephone disponible pour ce client', 'warning');
      return;
    }

    setSmsComposer({
      open: true,
      message: buildDirectSmsBody(client.client_name),
      targetClient: client,
    });
  };

  const closeSmsComposer = () => {
    if (sendingBulkSms) {
      return;
    }

    setSmsComposer({
      open: false,
      message: DEFAULT_BULK_SMS_BODY,
      targetClient: null,
    });
  };

  const submitBulkSms = async (e: React.FormEvent) => {
    e.preventDefault();

    const message = smsComposer.message.trim();
    const targetClient = smsComposer.targetClient;
    if (!message) {
      showToast('Veuillez saisir le texte du SMS', 'warning');
      return;
    }

    try {
      setSendingBulkSms(true);
      const response = targetClient
        ? await clientsApi.sendSms(targetClient.id, message)
        : await clientsApi.sendBulkSms(
          activeContractClients.map((client) => client.id),
          message
        );
      const resultMessage = response.data?.message || (targetClient ? 'SMS envoye avec succes' : 'SMS en bloc traite avec succes');
      const hasPartialIssue = targetClient
        ? false
        : (response.data?.failed_count || 0) > 0 || (response.data?.skipped_count || 0) > 0;
      setSmsComposer({
        open: false,
        message: DEFAULT_BULK_SMS_BODY,
        targetClient: null,
      });
      showToast(resultMessage, hasPartialIssue ? 'warning' : 'success');
      await loadCommunicationClients();
    } catch (err: any) {
      showToast(
        err?.response?.data?.error || err?.response?.data?.message || 'Erreur lors de l envoi des SMS en bloc',
        'error'
      );
    } finally {
      setSendingBulkSms(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Gestion des Clients</h1>
        {(canBulkCommunicate || hasPermission('clients', 'create')) && (
          <div className="header-actions">
            {canBulkCommunicate && (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleBulkEmail}
                  disabled={activeContractEmailClients.length === 0 || sendingBulkEmail}
                >
                  {sendingBulkEmail
                    ? 'Envoi email en cours...'
                    : `Email contrats actifs (${activeContractEmailClients.length})`}
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleBulkSms}
                  disabled={activeContractClients.length === 0 || sendingBulkSms}
                >
                  {sendingBulkSms
                    ? 'Envoi SMS en cours...'
                    : `SMS contrats actifs (${activeContractClients.length})`}
                </button>
              </>
            )}
            {hasPermission('clients', 'create') && (
              <button className="btn btn-primary" onClick={handleAdd}>
                + Nouveau Client
              </button>
            )}
          </div>
        )}
      </div>

      <div className="filters">
        <input
          type="text"
          placeholder="Rechercher par nom, telephone ou email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
        <select
          value={contractFilter}
          onChange={(e) => setContractFilter(e.target.value as ClientContractFilter)}
        >
          <option value="all">Tous les clients</option>
          <option value="with_contrats">Clients avec contrats</option>
          <option value="without_contrats">Clients sans contrats</option>
        </select>
        {canSendRemote && (
          <select
            value={remoteFilter}
            onChange={(e) => setRemoteFilter(e.target.value as ClientRemoteFilter)}
            aria-label="Filtrer selon l'envoi vers Remote"
          >
            <option value="all">Remote : tous</option>
            <option value="non_envoyes">Non envoyes (absents de Remote)</option>
            <option value="refuses">Refuses au dernier envoi</option>
            <option value="envoyes">Envoyes (presents dans Remote)</option>
          </select>
        )}
      </div>

      {(canBulkCommunicate || canSendRemote) && (
        <div className="bulk-selection-bar">
          <span className="bulk-selection-info">
            {filteredClients.length} client{filteredClients.length > 1 ? 's' : ''} affiche{filteredClients.length > 1 ? 's' : ''}
            {canBulkCommunicate && (
              <>
                {' '}| {activeContractClients.length} client{activeContractClients.length > 1 ? 's' : ''} avec contrat actif
              </>
            )}
            {canSendRemote && (
              <>
                {' '}| {selectedIds.size} selectionne{selectedIds.size > 1 ? 's' : ''}
              </>
            )}
          </span>
          {canSendRemote && (
            <div className="bulk-selection-actions">
              {selectedIds.size > 0 && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setSelectedIds(new Set())}
                  disabled={sendingRemote}
                >
                  Vider la selection
                </button>
              )}
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSendToRemote}
                disabled={selectedIds.size === 0 || sendingRemote}
                title="Creer ou mettre a jour les tiers des clients selectionnes dans Remote"
              >
                {sendingRemote ? 'Envoi vers Remote...' : `Envoyer vers Remote (${selectedIds.size})`}
              </button>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="loading">Chargement...</div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                {canSendRemote && (
                  <th className="checkbox-cell">
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate = selectedFilteredCount > 0 && !allFilteredSelected;
                        }
                      }}
                      onChange={toggleAllFiltered}
                      disabled={filteredClients.length === 0 || sendingRemote}
                      title="Selectionner tous les clients filtres"
                      aria-label="Selectionner tous les clients filtres"
                    />
                  </th>
                )}
                <th>Nom</th>
                <th>Telephone</th>
                <th>Email</th>
                <th>Wilaya</th>
                <th>Ville</th>
                {canSendRemote && <th>Remote</th>}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedClients.length === 0 ? (
                <tr>
                  <td colSpan={canSendRemote ? 8 : 6} style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    {search || contractFilter !== 'all' || activeRemoteFilter !== 'all'
                      ? 'Aucun client trouve pour ces filtres'
                      : 'Aucun client enregistre'}
                  </td>
                </tr>
              ) : (
                paginatedClients.map((client) => (
                  <tr key={client.id}>
                    {canSendRemote && (
                      <td className="checkbox-cell">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(client.id)}
                          onChange={() => toggleClientSelection(client.id)}
                          disabled={sendingRemote}
                          aria-label={`Selectionner ${client.client_name}`}
                        />
                      </td>
                    )}
                    <td>{client.client_name}</td>
                    <td>{client.telephone}</td>
                    <td>{client.email}</td>
                    <td>{client.wilaya}</td>
                    <td>{client.ville}</td>
                    {canSendRemote && <td>{renderRemoteCell(client)}</td>}
                    <td>
                      <div className="action-buttons">
                        {canBulkCommunicate && (
                          <>
                            <button
                              className="btn-icon btn-icon-mail"
                              onClick={() => handleSingleEmail(client)}
                              title={client.email?.trim() ? 'Envoyer un email' : 'Aucune adresse email'}
                              aria-label={`Envoyer un email a ${client.client_name}`}
                              disabled={!client.email?.trim()}
                            >
                              <MailIcon />
                            </button>
                            <button
                              className="btn-icon btn-icon-sms"
                              onClick={() => handleSingleSms(client)}
                              title={client.telephone?.trim() ? 'Envoyer un SMS' : 'Aucun numero de telephone'}
                              aria-label={`Envoyer un SMS a ${client.client_name}`}
                              disabled={!client.telephone?.trim()}
                            >
                              <SmsIcon />
                            </button>
                          </>
                        )}
                        <button
                          className="btn-icon"
                          onClick={() => setAuditClient(client)}
                          title="Historique"
                          aria-label={`Historique ${client.client_name}`}
                        >
                          <HistoryIcon />
                        </button>
                        {hasPermission('clients', 'edit') && (
                          <button
                            className="btn-icon"
                            onClick={() => handleEdit(client)}
                            title="Modifier"
                            aria-label={`Modifier ${client.client_name}`}
                          >
                            <EditIcon />
                          </button>
                        )}
                        {canResetPortalAccess && (
                          <button
                            className="btn-icon"
                            onClick={() => handleResetPortalAccess(client)}
                            title="Reinitialiser l'acces au portail client (mot de passe 1)"
                            aria-label={`Reinitialiser l'acces portail de ${client.client_name}`}
                          >
                            <KeyIcon />
                          </button>
                        )}
                        {hasPermission('clients', 'delete') && (
                          <button
                            className="btn-icon"
                            onClick={() => handleDelete(client.id)}
                            title="Supprimer"
                            aria-label={`Supprimer ${client.client_name}`}
                          >
                            <DeleteIcon />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && filteredClients.length > ITEMS_PER_PAGE && (
        <div className="pagination">
          <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
            {'<<'}
          </button>
          <button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
            {'<'}
          </button>

          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum: number;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={currentPage === pageNum ? 'active' : ''}
              >
                {pageNum}
              </button>
            );
          })}

          <button onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>
            {'>'}
          </button>
          <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
            {'>>'}
          </button>

          <span className="pagination-info">
            {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, filteredClients.length)} sur {filteredClients.length}
          </span>
        </div>
      )}

      {remoteResults && (
        <div className="modal-overlay">
          <div className="modal modal-wide">
            <h2>Envoi vers Remote</h2>
            <p className="info-message">
              {(() => {
                const refuses = remoteResults.filter((r) => r.resultat === 'refuse').length;
                const introuvables = remoteResults.filter((r) => r.resultat === 'introuvable').length;
                const acceptes = remoteResults.length - refuses - introuvables;
                return (
                  `${acceptes} client${acceptes > 1 ? 's' : ''} accepte${acceptes > 1 ? 's' : ''}` +
                  (refuses > 0 ? `, ${refuses} refuse${refuses > 1 ? 's' : ''} (corrigez la fiche puis renvoyez)` : '') +
                  (introuvables > 0 ? `, ${introuvables} introuvable${introuvables > 1 ? 's' : ''}` : '') +
                  '.'
                );
              })()}
            </p>
            <div className="table-container remote-tiers-results">
              <table>
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Resultat</th>
                  </tr>
                </thead>
                <tbody>
                  {remoteResults.map((r) => (
                    <tr key={r.client_id}>
                      <td>{r.client_name || `Client ${r.client_id}`}</td>
                      <td>
                        {r.resultat === 'refuse' ? (
                          <>
                            <span className="status-badge inactive">Refuse</span>{' '}
                            {remoteErreurLabel(r.error)}
                          </>
                        ) : (
                          <>
                            {REMOTE_RESULTAT_LABELS[r.resultat] || r.resultat}
                            {r.archive ? ' (tiers archive dans Remote, il le reste)' : ''}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-primary" onClick={() => setRemoteResults(null)}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {auditClient && (
        <AuditHistoryModal
          tableName="clients"
          recordId={auditClient.id}
          recordLabel={auditClient.client_name}
          fieldLabels={{
            client_name: 'Nom',
            telephone: 'Telephone',
            email: 'Email',
            wilaya: 'Wilaya',
            ville: 'Ville',
          }}
          onClose={() => setAuditClient(null)}
        />
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{editingClient ? 'Modifier le client' : 'Nouveau client'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nom du client</label>
                <input
                  type="text"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Telephone</label>
                  <input
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Optionnel"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Wilaya</label>
                  <select
                    value={formData.wilaya}
                    onChange={(e) => setFormData({ ...formData, wilaya: e.target.value })}
                    required
                  >
                    <option value="">Selectionner</option>
                    {WILAYAS.map((wilaya) => (
                      <option key={wilaya} value={wilaya}>{wilaya}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Ville</label>
                  <input
                    type="text"
                    value={formData.ville}
                    onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingClient ? 'Mettre a jour' : 'Creer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {emailComposer.open && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{emailComposer.targetClient ? `Envoyer un email a ${emailComposer.targetClient.client_name}` : 'Email en bloc'}</h2>
            <form onSubmit={submitBulkEmail}>
              <p className="info-message">
                {emailComposer.targetClient
                  ? `Le message sera envoye par le serveur SMTP a ${emailComposer.targetClient.client_name} (${emailComposer.targetClient.email}).`
                  : `Le message sera envoye par le serveur SMTP a ${activeContractEmailClients.length} client(s) avec contrat actif et email disponible.`}
              </p>
              <div className="form-group">
                <label>Sujet</label>
                <input
                  type="text"
                  value={emailComposer.subject}
                  onChange={(e) => setEmailComposer((prev) => ({ ...prev, subject: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Message</label>
                <textarea
                  value={emailComposer.message}
                  onChange={(e) => setEmailComposer((prev) => ({ ...prev, message: e.target.value }))}
                  rows={8}
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeEmailComposer} disabled={sendingBulkEmail}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary" disabled={sendingBulkEmail}>
                  {sendingBulkEmail ? 'Envoi en cours...' : 'Envoyer l email'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {smsComposer.open && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{smsComposer.targetClient ? `Envoyer un SMS a ${smsComposer.targetClient.client_name}` : 'SMS en bloc'}</h2>
            <form onSubmit={submitBulkSms}>
              <p className="info-message">
                {smsComposer.targetClient
                  ? `Le meme texte sera envoye a ${smsComposer.targetClient.client_name} (${smsComposer.targetClient.telephone}).`
                  : `Le meme texte sera envoye a ${activeContractClients.length} client(s) ayant un contrat actif non expire.`}
              </p>
              <div className="form-group">
                <label>Texte du SMS</label>
                <textarea
                  value={smsComposer.message}
                  onChange={(e) => setSmsComposer((prev) => ({ ...prev, message: e.target.value }))}
                  rows={6}
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeSmsComposer} disabled={sendingBulkSms}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-success" disabled={sendingBulkSms}>
                  {sendingBulkSms ? 'Envoi en cours...' : 'Envoyer le SMS'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
